import React, { useState } from 'react';
import { useAuthContext, AccessRole } from '../hooks/AuthContext';

const ROLES: { value: AccessRole | 'reset'; label: string; icon: string }[] = [
  { value: 'reset', label: 'My Role', icon: '👤' },
  { value: 'admin', label: 'Admin', icon: '👑' },
  { value: 'developer', label: 'Developer', icon: '💻' },
  { value: 'viewer', label: 'Viewer', icon: '👁️' },
];

/**
 * Floating role switcher — only visible to admins.
 * Lets admins preview the app as a different role without changing their DB record.
 */
const RoleSwitcher: React.FC = () => {
  const { isAdmin, authUser, roleOverride, setRoleOverride } = useAuthContext();
  const [open, setOpen] = useState(false);

  // Only admins can see the switcher
  if (!isAdmin) return null;

  return (
    <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>
      {open && (
        <div style={{
          background: '#1a1a2e',
          border: '1px solid #333',
          borderRadius: 10,
          padding: 16,
          marginBottom: 8,
          width: 200,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
        }}>
          <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.5px' }}>
            View As Role
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {ROLES.map(r => {
              const isActive = r.value === 'reset'
                ? !roleOverride
                : roleOverride === r.value;
              return (
                <button
                  key={r.value}
                  onClick={() => setRoleOverride(r.value === 'reset' ? null : r.value as AccessRole)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: '0.8rem',
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: isActive ? '1px solid #4361ee' : '1px solid #444',
                    background: isActive ? '#4361ee22' : 'transparent',
                    color: isActive ? '#7c9dff' : '#ccc',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <span>{r.icon}</span>
                  <span>{r.label}</span>
                  {r.value === 'reset' && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.65rem', opacity: 0.6 }}>
                      ({authUser?.access_role})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {roleOverride && (
            <div style={{ marginTop: 10, padding: '6px 8px', background: '#ff990022', borderRadius: 4, fontSize: '0.7rem', color: '#ffaa33' }}>
              ⚠️ Simulating <strong>{roleOverride}</strong> view
            </div>
          )}
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: roleOverride ? '#ff9900' : '#4361ee',
          color: 'white',
          border: 'none',
          fontSize: '1rem',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          transition: 'background-color 0.2s'
        }}
        title="Role Switcher (Admin Only)"
      >
        {roleOverride ? '🎭' : '⚙'}
      </button>
    </div>
  );
};

export default RoleSwitcher;
